import { useState, useEffect } from "react";
import { useModal } from "hooks/useModal";
import { useStripeAccount } from "features/stripe/useStripeAccount";
import { consumeOnboardingReturn } from "features/stripe/onboardingReturn";
import {
  resolveSettlementRoute,
  BLOCKED_ACCOUNT_MESSAGE,
  UNKNOWN_STATUS_MESSAGE,
  OnboardingVariant,
  SettlementRoute,
} from "features/stripe/settlementRouting";
import { useColumnCounterparty } from "features/column/useColumnCounterparty";
import { SettlementAccountStatus } from "types/stripe";
import {
  ColumnCounterpartyFormData,
  CreateColumnCounterpartyInput,
} from "types/column";
import { apiErrorMessage } from "utils/errors/parseApiError";

const PENDING_REVIEW_MESSAGE =
  "The information you provided is still being reviewed by our payment " +
  "provider. There's nothing you need to do — you'll be able to redeem as " +
  "soon as the review completes.";

type SubmitRedeemFn = (
  payload: {
    merchantId: string;
    treasuryId: string;
    amountInVip: number;
    settlementToken?: string;
  },
  callbacks: { onSuccess: () => void; onError: (err: any) => void },
) => void;

interface UseBalanceCtaOptions {
  merchantId: string;
  treasuryId: string;
  /** Available balance IN THE SELECTED TOKEN, already net of that token's pending redeems. */
  balance: number;
  /** Selected token's label, for the guard message. */
  tokenSymbol: string;
  /** Selected token's address; omitted => the contract's default. */
  settlementToken?: string;
  pendingCount: number;
  // True while pendingCount / balance are still being fetched. Only the
  // Stripe-return path reads it — see the effect below.
  guardsLoading?: boolean;
  submitRedeem: SubmitRedeemFn;
}

export function useBalanceCta({
  merchantId,
  treasuryId,
  balance,
  tokenSymbol,
  settlementToken,
  pendingCount,
  guardsLoading = false,
  submitRedeem,
}: UseBalanceCtaOptions) {
  const { checkAccount, createAccount, getDashboardLink } = useStripeAccount();
  const { createCounterparty, creating: columnSubmitting } =
    useColumnCounterparty();

  const {
    showModal: showRedeem,
    openModal: openRedeemModal,
    closeModal: closeRedeemModal,
  } = useModal();
  const {
    showModal: showRedeemBlocked,
    openModal: openRedeemBlockedModal,
    closeModal: closeRedeemBlockedModal,
  } = useModal();
  const {
    showModal: showColumnForm,
    openModal: openColumnFormModal,
    closeModal: closeColumnFormModal,
  } = useModal();
  const {
    showModal: showStripeOnboarding,
    openModal: openStripeOnboardingModal,
    closeModal: closeStripeOnboardingModal,
  } = useModal();
  const {
    showModal: showPendingReview,
    openModal: openPendingReviewModal,
    closeModal: closePendingReviewModal,
  } = useModal();
  const {
    showModal: showLoading,
    openModal: openLoadingModal,
    closeModal: closeLoadingModal,
  } = useModal();
  const {
    showModal: showSuccess,
    openModal: openSuccessModal,
    closeModal: closeSuccessModal,
  } = useModal();
  const {
    showModal: showError,
    openModal: openErrorModal,
    closeModal: closeErrorModal,
  } = useModal();

  const [loadingMsg, setLoadingMsg] = useState("Processing…");
  const [onboardingUrl, setOnboardingUrl] = useState("");
  const [onboardingVariant, setOnboardingVariant] =
    useState<OnboardingVariant>("required");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [redeemBlockedMessage, setRedeemBlockedMessage] = useState("");

  // The two rules that gate a redeem, in one place because they're applied in
  // two: at the CTA, and again in openRedeemFlow. The Stripe-onboarding return
  // reaches the redeem modal WITHOUT going through handleRedeemClick, and a
  // return can land — or the balance move — during the minutes of hosted KYC,
  // so the state that was checked on the way out is not the state on the way
  // back. The server enforces the pending-returns half independently.
  function redeemBlockedReason(): string | null {
    if (pendingCount > 0) {
      return `You have ${pendingCount} pending return${pendingCount !== 1 ? "s" : ""}. Please settle all pending returns before redeeming.`;
    }
    if (Number(balance) <= 0) {
      // Names the token: with a switcher, a merchant holding USDC but no VIP would otherwise
      // read "insufficient balance" while looking at a non-zero figure.
      return `Your available ${tokenSymbol} balance is insufficient to complete this redeem. Please ensure that you have enough available balance and try again.`;
    }
    return null;
  }

  // Payouts are live — open the redeem modal, unless a redeem-side rule says no.
  function openRedeemFlow() {
    const blocked = redeemBlockedReason();
    if (blocked) {
      setRedeemBlockedMessage(blocked);
      openRedeemBlockedModal();
      return;
    }
    openRedeemModal();
  }

  function reportError(err: any, fallback: string) {
    setErrorMsg(apiErrorMessage(err, fallback));
    openErrorModal();
  }

  function showMessageError(message: string) {
    setErrorMsg(message);
    openErrorModal();
  }

  // Open exactly one modal for an already-resolved route.
  function applyRoute(route: SettlementRoute) {
    switch (route.kind) {
      case "ready":
        openRedeemFlow();
        return;
      case "column_form":
        // Column has no hosted KYC — collect bank details in-app instead.
        openColumnFormModal();
        return;
      case "onboarding":
        setOnboardingUrl(route.url);
        setOnboardingVariant(route.variant);
        openStripeOnboardingModal();
        return;
      case "pending_review":
        openPendingReviewModal();
        return;
      case "blocked":
        showMessageError(BLOCKED_ACCOUNT_MESSAGE);
        return;
      // "create_stripe_account" is consumed by routeStatus below, so reaching it
      // here means a just-created account still reported no account.
      case "create_stripe_account":
      case "unknown":
      default:
        showMessageError(UNKNOWN_STATUS_MESSAGE);
        return;
    }
  }

  // Resolve a status to a route and act on it, creating the Stripe account first
  // if there isn't one yet (KYC is Stripe-hosted, so creation yields an
  // onboarding link rather than working payouts). The caller owns the loading
  // modal — it's already open on every path that gets here.
  async function routeStatus(status: SettlementAccountStatus) {
    let route = resolveSettlementRoute(status);

    if (route.kind === "create_stripe_account") {
      setLoadingMsg("Creating payout account…");
      const created = await createAccount(merchantId);
      route = resolveSettlementRoute(created);
      // A freshly created account cannot need creating again. Bail to the error
      // path rather than looping on a status we evidently can't interpret.
      if (route.kind === "create_stripe_account") route = { kind: "unknown" };
    }

    applyRoute(route);
  }

  // Re-check account status when the merchant returns from Stripe onboarding
  // that started at THIS CTA (mid-redeem) — settings has its own handler, and
  // consumeOnboardingReturn gives the return to only one of them. It also covers
  // stripe_refresh (link expired or already used before they finished): same
  // handling, since checkAccount mints a fresh link and the route reopens the
  // modal with it.
  //
  // Wait for pendingCount / balance before touching the return. This runs on
  // a fresh page load with both queries still in flight, where they read as
  // "0 pending, 0 balance" — which would wave a blocked merchant through the
  // pending-returns rule and falsely block everyone on the balance one. Holding
  // off is safe: nothing else strips the return params, so the effect just
  // picks it up on the render after the data lands.
  useEffect(() => {
    if (!merchantId || guardsLoading) return;
    if (!consumeOnboardingReturn("redeem")) return;

    (async () => {
      setLoadingMsg("Checking payout account…");
      openLoadingModal();
      try {
        const status = await checkAccount(merchantId);
        await routeStatus(status);
      } catch (err: any) {
        reportError(err, "Failed to check payout account. Please try again.");
      } finally {
        closeLoadingModal();
      }
    })();
  }, [merchantId, guardsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleRedeemClick() {
    const blocked = redeemBlockedReason();
    if (blocked) {
      setRedeemBlockedMessage(blocked);
      openRedeemBlockedModal();
      return;
    }

    setLoadingMsg("Checking payout account…");
    openLoadingModal();
    try {
      const status = await checkAccount(merchantId);
      await routeStatus(status);
    } catch (err: any) {
      reportError(err, "Failed to check payout account. Please try again.");
    } finally {
      closeLoadingModal();
    }
  }

  // Only offered for the "optional" onboarding variant, where payouts already
  // work and Stripe is merely asking for more before a future deadline. Blocking
  // the redeem there would deny a merchant we can pay today, so let them carry on
  // — through the same redeem rules as any other route to the modal.
  function handleOnboardingSkip() {
    closeStripeOnboardingModal();
    openRedeemFlow();
  }

  // From the Column bank-details modal: create the counterparty, then route.
  // Column has no onboarding step, so a success enables payouts → redeem.
  async function handleColumnSubmit(formData: ColumnCounterpartyFormData) {
    closeColumnFormModal();
    setLoadingMsg("Saving bank account…");
    openLoadingModal();

    const input: CreateColumnCounterpartyInput = {
      ...formData,
      country: "US",
    };

    try {
      const status = await createCounterparty({ merchantId, input });
      await routeStatus(status);
    } catch (err: any) {
      reportError(err, "Failed to save bank account. Please try again.");
    } finally {
      closeLoadingModal();
    }
  }

  function handleRedeemConfirm(amount: number) {
    closeRedeemModal();
    setLoadingMsg("Processing…");
    openLoadingModal();
    submitRedeem(
      { merchantId, treasuryId, amountInVip: amount, settlementToken },
      {
        onSuccess: () => {
          closeLoadingModal();
          setSuccessMsg(
            "Redeem request submitted. Awaiting treasury approval.",
          );
          openSuccessModal();
        },
        onError: (err: any) => {
          closeLoadingModal();
          reportError(
            err,
            "Failed to submit redeem request. Please try again.",
          );
        },
      },
    );
  }

  async function handlePayoutsClick() {
    setPayoutsLoading(true);
    try {
      const url = await getDashboardLink(merchantId);
      window.open(url, "_blank");
    } catch (err: any) {
      reportError(err, "Failed to open payout dashboard. Please try again.");
    } finally {
      setPayoutsLoading(false);
    }
  }

  return {
    handleRedeemClick,
    handleRedeemConfirm,
    handleColumnSubmit,
    handlePayoutsClick,
    payoutsLoading,
    columnSubmitting,
    balance,
    pendingCount,
    onboardingUrl,
    onboardingVariant,
    // Undefined for the "required" variant: there's nothing to skip to when
    // payouts don't work yet, so the button just closes the modal.
    onboardingSkip:
      onboardingVariant === "optional" ? handleOnboardingSkip : undefined,
    onboardingSkipLabel:
      onboardingVariant === "optional" ? "Continue to redeem" : undefined,
    loadingMsg,
    errorMsg,
    successMsg,
    pendingReviewMsg: PENDING_REVIEW_MESSAGE,
    showRedeem,
    closeRedeemModal,
    showRedeemBlocked,
    redeemBlockedMessage,
    closeRedeemBlockedModal,
    showColumnForm,
    closeColumnForm: closeColumnFormModal,
    showStripeOnboarding,
    closeStripeOnboardingModal,
    showPendingReview,
    closePendingReviewModal,
    showLoading,
    showSuccess,
    closeSuccessModal,
    showError,
    closeErrorModal,
  };
}
